import { useState } from 'react';

import { faFileJpg } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import {
  faCircleInfo,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/ui/tooltip';
import classNames from 'classnames';
import { isSameDay, startOfDay } from 'date-fns';
import { useSetAtom } from 'jotai';

import { selectedVisitAtom } from '../../../../app/appHeader/imageViewerMenu/atoms';
import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import { useGetDateFNSLocale } from '../../../../locales/helpers';
import type { Appointment } from '../../../../react-query/useAppointments';
import type { TFmxSummary } from '../../../../react-query/useFmxSummaries';
import type { GetTaskResponse } from '../../../../react-query/useGetTasks';
import { usePreferences } from '../../../../react-query/usePreferences';
import { useDevtoolsShortcut } from '../../../../utils/hooks/useDevtoolsShortcut';
import { useIsInsurancePDFEnabled } from '../../../../utils/hooks/useIsInsurancePDFEnabled';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { useGetModelFeatureFlags } from '../../../annotator/utils';
import {
  claimSummaryDialogAtom,
  useLocalizedFormatDate,
} from '../../../claims/freeClaims/state';
import { getDisplayName, useIsScheduleViewEnabled } from '../../helpers';
import { getProviderName } from '../../huddlePrintoutContent/helpers';
import { computeTreatmentDateAndOpportunityString } from '../imagesInfo/helpers';
import InfoDialog from '../InfoDialog';
import { DateDisplay } from '../patientStripRedesigned/DateDisplay';
import { ReferralStatusIcon } from '../patientStripRedesigned/ReferralStatusIcon';
import { ReviewFindingsButton } from '../patientStripRedesigned/ReviewFindingsButton';
import { useReviewRadiographs } from '../useReviewRadiographs';

import { FindingChip } from './findingChip';
import { useGenerateFindingsCounts } from './helpers';
import { InsurancePDFExport } from './insurancePDFExport/insurancePDFExport';

const TypedFindings = (props: {
  findingType: string;
  findingsCounts: ReturnType<
    ReturnType<typeof useGenerateFindingsCounts>['generateFindingsCounts']
  >;

  imagesDate: string | null;
  patientId: string;
}) => {
  const { findingType, findingsCounts, imagesDate } = props;

  const hasRestoFindings =
    (findingsCounts?.allRestoFindingCounts?.pathologyCount || 0) > 0;
  const hasPerioFindings = !!findingsCounts?.perioAllQuadrants;

  const hasFindings = hasRestoFindings || hasPerioFindings;
  if (!hasFindings) {
    return null;
  }

  return (
    <>
      {findingType === 'Resto' && hasRestoFindings && (
        <>
          <VideaTypography
            className="vai-typography-paragraph-small-strong vai-text-gray-500 vai-pb-2"
            variant="labelSmallStrong"
          >
            <Trans>Resto</Trans>
          </VideaTypography>
          <FindingChip
            label=""
            type={findingType}
            imagesDate={imagesDate}
            unplannedFindings={true}
            toothNumbers={findingsCounts?.allRestoFindingCounts?.toothNumbers}
            pathologyCount={
              findingsCounts?.allRestoFindingCounts?.pathologyCount
            }
            className="condensed-chip"
          />
        </>
      )}
      {findingType === 'Perio' && hasPerioFindings && (
        <>
          <VideaTypography
            className="vai-typography-paragraph-small-strong vai-text-gray-500 vai-pb-2"
            variant="labelSmallStrong"
          >
            <Trans>Perio</Trans>
          </VideaTypography>
          <FindingChip
            type={findingType}
            quadrantColors={findingsCounts.perioAllQuadrants}
            className="condensed-chip"
          />
        </>
      )}
    </>
  );
};

export const Findings = ({
  appointment,
  fmxSummary,
  onReviewRadiographs,
  patientTask,
  onOpenReferModal,
  onFlagReferral,
  onUnmarkReferral,
  onResubmitReferral,
}: {
  appointment: Appointment;
  fmxSummary?: TFmxSummary;
  onReviewRadiographs: () => void;
  patientTask?: GetTaskResponse;
  onOpenReferModal: () => void;
  onFlagReferral?: () => void;
  onUnmarkReferral?: () => void;
  onResubmitReferral?: () => void;
}) => {
  const setDialogValues = useSetAtom(claimSummaryDialogAtom);
  const setSelectedVisit = useSetAtom(selectedVisitAtom);
  const isScheduleViewEnabled = useIsScheduleViewEnabled();
  const isInsurancePDFEnabled = useIsInsurancePDFEnabled();
  const { generateFindingsCounts } = useGenerateFindingsCounts();
  const findingsCounts = generateFindingsCounts(fmxSummary);
  const { isRblEnabled } = useGetModelFeatureFlags();
  const [showInfo, setShowInfo] = useState(false);
  const { trackEvent } = useTrackEvent();
  const { showDevtools } = useDevtoolsShortcut();

  const [isOpenDevTools, setIsOpenDevTools] = useState(false);

  const imagesDate =
    (!!appointment.appointment.imagesAt &&
      computeTreatmentDateAndOpportunityString(
        new Date(appointment.appointment.imagesAt),
        true,
      )) ||
    null;

  const today = new Date();
  const estimatedVisitDate =
    !!appointment.appointment.imagesAt &&
    new Date(appointment.appointment.imagesAt);
  const { data: prefs } = usePreferences();
  const showInsModalButton =
    isInsurancePDFEnabled &&
    imagesDate &&
    (prefs?.cleanClaims || prefs?.insSummaryModal);

  const formatDate = useLocalizedFormatDate();
  const locale = useGetDateFNSLocale();

  const { annotatorUri } = useReviewRadiographs(appointment);

  const handleClickOpen = () => {
    const estimatedVisitId =
      appointment.appointment.estimatedVisitId?.toString() ?? null;
    setSelectedVisit(estimatedVisitId);

    setDialogValues({
      isOpen: true,
      unifiedId: appointment.appointment.unifiedId,
      view: 'image',
      insView: 'preview-pages',
      appointment,
      imagingPatientId: appointment.patient.imagingPatientId ?? '',
      estimatedVisitId: estimatedVisitId ?? '',
      patientInfo: {
        name: getDisplayName(appointment, 0).patientName,
        dob: formatDate(appointment.patient.dob ?? '', true),
        dateOfVisit: formatDate(appointment?.appointment.start || ''),
        radiographsTakenDate: appointment.appointment.imagesAt ?? '',
        providerName: getProviderName(appointment?.appointment.provider),
      },
      locale,
    });

    trackEvent(mixpanelEvents.insuranceSummary, {
      source: 'summary-only button',
    });
  };

  /**
   * If the estimated visit date is today, show the findings with 'real-time' style,
   * otherwise show the 'planning' style
   */
  const isRealTime =
    !!estimatedVisitDate && isSameDay(estimatedVisitDate, startOfDay(today));
  const isReview =
    !isScheduleViewEnabled &&
    !!estimatedVisitDate &&
    !isSameDay(estimatedVisitDate, startOfDay(today));
  const isNoImages = !appointment.patient.imagingPatientId;

  return (
    <div
      onClick={() => setShowInfo(true)}
      onKeyDown={() => setShowInfo(true)}
      className={classNames(
        'vai-grid vai-grid-cols-4 vai-w-1/2 vai-items-center vai-px-4',
        isRealTime &&
          `vai-bg-gradient-to-b vai-from-[#eaf0ff] vai-to-[#e0e9ff]`,
        isReview && 'vai-bg-slate-50',
        isNoImages && 'vai-bg-transparent vai-flex vai-justify-end ',
      )}
    >
      {isRblEnabled && (
        <div className="vai-flex vai-flex-col vai-flex-wrap vai-w-48">
          <TypedFindings
            findingType="Perio"
            findingsCounts={findingsCounts}
            imagesDate={imagesDate}
            patientId={appointment.patient.pmsPatientId || ''}
          />
        </div>
      )}
      <div className="vai-flex vai-flex-col vai-flex-wrap vai-w-48">
        <TypedFindings
          findingType="Resto"
          findingsCounts={findingsCounts}
          imagesDate={imagesDate}
          patientId={appointment.patient.pmsPatientId || ''}
        />
      </div>
      <div className="vai-flex vai-items-center vai-gap-2">
        {showDevtools && (
          <>
            <FontAwesomeIcon
              className="vai-w-4 vai-h-4 vai-text-blue-600 vai-cursor-pointer"
              icon={faExclamationCircle}
              onClick={() => {
                setIsOpenDevTools(true);
              }}
            />
            <InfoDialog
              appointment={appointment}
              onClose={() => setIsOpenDevTools(false)}
              isOpen={isOpenDevTools}
              patientTask={patientTask}
            />
          </>
        )}
      </div>
      <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center vai-w-full vai-justify-end">
        {isInsurancePDFEnabled &&
          imagesDate &&
          !!prefs &&
          !prefs?.cleanClaims &&
          !prefs.insSummaryModal && (
            <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
              <InsurancePDFExport appointment={appointment} />
            </div>
          )}
        {showInsModalButton && (
          <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" onClick={handleClickOpen}>
                  <FontAwesomeIcon icon={faFileJpg} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <Trans>Generate Insurance JPGs</Trans>
              </TooltipContent>
            </Tooltip>
          </div>
        )}
        {prefs?.enableVideaReferral && (
          <div className="vai-flex vai-flex-row vai-gap-2 vai-items-center vai-mt-2">
            <ReferralStatusIcon
              unifiedAppointmentId={appointment.appointment.unifiedId}
              onOpenReferModal={onOpenReferModal}
              onFlagReferral={onFlagReferral}
              onUnmarkReferral={onUnmarkReferral}
              onResubmitReferral={onResubmitReferral}
            />
          </div>
        )}
        <div className="vai-flex vai-flex-col vai-gap-2">
          {appointment.patient.imagingPatientId ? (
            <ReviewFindingsButton
              appointment={appointment}
              onReviewRadiographs={onReviewRadiographs}
              annotatorUri={annotatorUri}
              source="daily-dashboard-huddle-hidden"
            />
          ) : (
            <div className="vai-flex vai-items-center vai-gap-2 vai-typography-muted">
              <FontAwesomeIcon icon={faCircleInfo} />
              <Trans>No images available</Trans>
            </div>
          )}
          {appointment.appointment.imagesAt && (
            <DateDisplay date={appointment.appointment.imagesAt} />
          )}
        </div>
      </div>
    </div>
  );
};
